import { z } from 'zod'

import { fromProblemV2, ProblemSourceTypeSchema, ProblemV2WireSchema } from '~/@entities/Problem'
import type { AllGradeSubject, Revision, SchoolType, Semester } from '~/@entities/SchoolSystem'
import {
  formatWorkbookScoringGrade,
  type ScoringItemInput,
  toScoringItem,
} from '~/@entities/Scoring'
import {
  toWorkbookProblem,
  withWorkbookCoverDefaults,
  type Workbook,
  WorkbookProblemWireSchema,
  WorkbookSchema,
} from '~/@entities/Workbook'
import type {
  CommonResponseSliceGroupWorkbookPageToStudentWorkbookProgressResponse,
  CommonResponseSliceGroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse,
  CommonResponseSliceGroupWorkbookToRoundToStudentWorkbookResponse,
  SetProgressOfPageData,
  SliceWorkbookAndStudentWorkbookResponse,
  SliceWorkbookProblemToStudentWorkbookScoringResponse,
  WorkbookAndRoundAndStudentWorkbookProgressWithWorkbookPageResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'
import { mapSliceContent, type Slice, sliceSchema } from '~/schema/slice'

import {
  type GroupStudentWorkbook,
  GroupStudentWorkbookSchema,
  type GroupWorkbookPageProgress,
  GroupWorkbookPageProgressSchema,
  type GroupWorkbookScoredProblem,
  GroupWorkbookScoredProblemSchema,
  type StudentWorkbookListItem,
  type StudentWorkbookListItemDetail,
  StudentWorkbookListItemDetailSchema,
  StudentWorkbookListItemSchema,
  type StudentWorkbookProgressList,
  StudentWorkbookProgressListSchema,
  type StudentWorkbookScoredProblem,
  type StudentWorkbookScoredProblemDetail,
  StudentWorkbookScoredProblemDetailSchema,
  StudentWorkbookScoredProblemSchema,
  type StudentWorkbookScoring,
  type StudentWorkbookScoringProblem,
  toWorkbookScoringId,
  type WorkbookScoringProblem,
} from './schemas'

const ENTITY = 'StudentWorkbook'

// 세 조회 모두 교재(WorkbookDto)·교재 문제(WorkbookProblemDto)를 품는데, 스펙은 그 하위 클래스를 상위 클래스로만 그려
// 종류별 필드가 없다(Workbook 엔티티 adapter 와 같은 결함). 목록은 교재 필드를 펼쳐(@JsonUnwrapped) 싣고,
// 채점의 userAnswers 는 서버가 저장 문자열을 풀어 배열로 낸다(@get:JsonProperty("userAnswers")).
// 2026-10-08 백엔드 develop 의 studentWorkbook/controller/response/WorkbookAndStudentWorkbookResponse.kt·
// WorkbookAndRoundAndStudentWorkbookProgressWithWorkbookPageResponse.kt·
// WorkbookProblemToStudentWorkbookScoringResponse.kt·model/dto/StudentWorkbookScoringDto.kt 로 확인.
const asWire = (raw: unknown): unknown => raw

// 교재의 표지 기본값 채우기와 목록 줄 분해는 검증 뒤 일반 함수로 한다(Workbook adapter 와 같은 이유)
const _StudentWorkbookListItemWireSchema = z.intersection(
  WorkbookSchema,
  StudentWorkbookListItemSchema.omit({ workbook: true }),
)

const toStudentWorkbookListItem = ({
  studentWorkbook,
  recentRevisionId,
  recentRevisionRound,
  recentPageNumber,
  recentAssignedDatetime,
  roundToRevisionRoundMap,
  ...workbook
}: z.output<typeof _StudentWorkbookListItemWireSchema>): StudentWorkbookListItem => ({
  workbook: withWorkbookCoverDefaults(workbook as Workbook),
  studentWorkbook,
  recentRevisionId,
  recentRevisionRound,
  recentPageNumber,
  recentAssignedDatetime,
  roundToRevisionRoundMap,
})

const _StudentWorkbookScoredProblemWireSchema = WorkbookProblemWireSchema.extend({
  scoring: StudentWorkbookScoredProblemSchema.shape.scoring,
  conceptHidden: StudentWorkbookScoredProblemSchema.shape.conceptHidden,
})

export const adaptStudentWorkbookList = (
  raw: SliceWorkbookAndStudentWorkbookResponse | undefined,
): Slice<StudentWorkbookListItem> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_StudentWorkbookListItemWireSchema), asWire(raw), ENTITY),
    toStudentWorkbookListItem,
  )

export const adaptStudentWorkbookProgressList = (
  raw: WorkbookAndRoundAndStudentWorkbookProgressWithWorkbookPageResponse | undefined,
): StudentWorkbookProgressList => {
  const progressList = parseApiResponse(StudentWorkbookProgressListSchema, asWire(raw), ENTITY)
  return progressList?.workbook
    ? { ...progressList, workbook: withWorkbookCoverDefaults(progressList.workbook) }
    : progressList
}

export const adaptStudentWorkbookScoredProblems = (
  raw: SliceWorkbookProblemToStudentWorkbookScoringResponse | undefined,
): Slice<StudentWorkbookScoredProblem> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_StudentWorkbookScoredProblemWireSchema), asWire(raw), ENTITY),
    toWorkbookProblem,
  )

export const toStudentWorkbookScoring = <P extends WorkbookScoringProblem>({
  workbook,
  problem,
  scoring,
}: {
  workbook: {
    autoScorable: boolean
    label: string
    schoolType?: SchoolType
    grade?: AllGradeSubject
    semester?: Semester
    revision?: Revision
  }
  problem: P
  scoring: ScoringItemInput<unknown, P>['record'] &
    Pick<StudentWorkbookScoring, 'workbookProblemId' | 'studentWorkbookProgressId'>
}): StudentWorkbookScoring<P> =>
  toScoringItem({
    target: {
      kind: 'WORKBOOK',
      id: toWorkbookScoringId(scoring),
      workbookProblemId: scoring.workbookProblemId,
      studentWorkbookProgressId: scoring.studentWorkbookProgressId,
    } as const,
    problem,
    record: scoring,
    content: {
      label: workbook.label,
      autoScorable: workbook.autoScorable,
      schoolType: workbook.schoolType,
      gradeLabel: formatWorkbookScoringGrade(workbook),
    },
    // 교재는 정답 개수 대신 정답 문자열의 칸 수를 센다
    answerLength: problem.answer.split(',').length,
  })

/**
 * 채점 조회의 교재 문제에 짝지은 문제은행 문제를 겹쳐 채점이 읽는 문제를 만든다.
 *
 * 키패드·답 단위·정답 AST 는 교재 문제 쪽 값을 쓴다. 짝지은 문제는 V2 모양이라 이 셋을 서버가 싣지 않거나
 * 다른 이름으로 싣고, fromProblemV2 가 채운 기본값(`[]`·null)이 교재 문제 값을 덮으면 수식 키패드가 숫자
 * 키패드로 떨어진다. 정답 AST 만은 교재 문제 쪽이 비었을 때 짝지은 문제 값으로 채운다.
 */
export const toStudentWorkbookScoringProblem = ({
  problem,
  xistory,
  ...workbookProblem
}: StudentWorkbookScoredProblem): StudentWorkbookScoringProblem => {
  const merged = { ...workbookProblem, ...problem, ...xistory?.problem }
  return {
    id: merged.id,
    problemId: workbookProblem.problemId ?? null,
    conceptId: merged.conceptId,
    answer: merged.answer,
    type: merged.type,
    keypadTypes: workbookProblem.keypadTypes,
    answerData:
      workbookProblem.answerData ?? xistory?.problem?.answerData ?? problem?.answerData ?? null,
    answerCount: merged.answerCount,
    answerUnits: workbookProblem.answerUnits,
    level: merged.level,
    optionCount: merged.optionCount,
    autoScoredType: merged.autoScoredType,
    answerImageUrl: merged.answerImageUrl,
    problemImageUrl: merged.problemImageUrl,
    solutionImageUrl: merged.solutionImageUrl,
    video: merged.video,
    videoMatched: 'videoMatched' in merged ? merged.videoMatched : undefined,
    answerConfirmed: workbookProblem.answerConfirmed,
    modifiedFromOriginalFlag: workbookProblem.modifiedFromOriginalFlag,
  }
}

/***************** 선생님 앱의 학생 교재 조회 *****************/
// 학년·반 목록은 학생 목록과 같이 교재 필드를 펼쳐(@JsonUnwrapped) 싣고, 학년·반 채점은 교재 문제를 펼쳐 싣는다.
// 스펙은 둘 다 교재·교재 문제의 상위 클래스만 그려(위 asWire 와 같은 결함) 입력을 unknown 으로 넘긴다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 studentWorkbook/controller/response/
// GroupWorkbookToRoundToStudentWorkbookResponse.kt·GroupWorkbookPageToStudentWorkbookProgressResponse.kt·
// GroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse.kt 로 확인.

const _StudentWorkbookListItemDetailWireSchema = z.intersection(
  WorkbookSchema,
  StudentWorkbookListItemDetailSchema.omit({ workbook: true }),
)

const toStudentWorkbookListItemDetail = ({
  originWorkbook,
  ...item
}: z.output<typeof _StudentWorkbookListItemDetailWireSchema>): StudentWorkbookListItemDetail => ({
  ...toStudentWorkbookListItem(item),
  originWorkbook,
})

// 내 교재·학원그룹 교재 문제가 품은 문제은행 문제(ProblemDto)는 V2 모양에 출처를 함께 싣는다.
const _WorkbookProblemSourceProblemWireSchema = ProblemV2WireSchema.extend({
  sourceType: ProblemSourceTypeSchema,
})

type _WithSourceProblemWire = {
  problem?: z.output<typeof _WorkbookProblemSourceProblemWireSchema> | null
}

const toWorkbookProblemDetail = <P extends _WithSourceProblemWire>({ problem, ...rest }: P) => {
  // rest 에는 problem 이 없지만 toWorkbookProblem 의 반환 타입이 V2 모양 problem 을 얹어 걷어낸다
  const { problem: _v2Problem, ...workbookProblem } = toWorkbookProblem(rest)
  return {
    ...workbookProblem,
    ...(problem !== undefined && {
      problem: problem && {
        ...fromProblemV2(problem),
        sourceType: problem.sourceType,
      },
    }),
  }
}

const _StudentWorkbookScoredProblemDetailWireSchema = WorkbookProblemWireSchema.extend({
  problem: _WorkbookProblemSourceProblemWireSchema.nullable().optional(),
  scoring: StudentWorkbookScoredProblemDetailSchema.shape.scoring,
  conceptHidden: StudentWorkbookScoredProblemDetailSchema.shape.conceptHidden,
  tagTop: StudentWorkbookScoredProblemDetailSchema.shape.tagTop,
})

const _GroupStudentWorkbookWireSchema = z.intersection(
  WorkbookSchema,
  GroupStudentWorkbookSchema.omit({ workbook: true }),
)

const toGroupStudentWorkbook = ({
  roundToDetailMap,
  originWorkbook,
  lastRound,
  recentAssignedDatetime,
  ...workbook
}: z.output<typeof _GroupStudentWorkbookWireSchema>): GroupStudentWorkbook => ({
  workbook: withWorkbookCoverDefaults(workbook as Workbook),
  roundToDetailMap,
  originWorkbook,
  lastRound,
  recentAssignedDatetime,
})

const _GroupWorkbookScoredProblemWireSchema = WorkbookProblemWireSchema.extend({
  problem: _WorkbookProblemSourceProblemWireSchema.nullable().optional(),
  tagTop: GroupWorkbookScoredProblemSchema.shape.tagTop,
  summary: GroupWorkbookScoredProblemSchema.shape.summary,
  list: GroupWorkbookScoredProblemSchema.shape.list,
})

export const adaptStudentWorkbookListDetail = (
  raw: SliceWorkbookAndStudentWorkbookResponse | undefined,
): Slice<StudentWorkbookListItemDetail> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_StudentWorkbookListItemDetailWireSchema), asWire(raw), ENTITY),
    toStudentWorkbookListItemDetail,
  )

export const adaptStudentWorkbookScoredProblemDetails = (
  raw: SliceWorkbookProblemToStudentWorkbookScoringResponse | undefined,
): Slice<StudentWorkbookScoredProblemDetail> =>
  mapSliceContent(
    parseApiResponse(
      sliceSchema(_StudentWorkbookScoredProblemDetailWireSchema),
      asWire(raw),
      ENTITY,
    ),
    toWorkbookProblemDetail,
  )

export const adaptGroupStudentWorkbooks = (
  raw: CommonResponseSliceGroupWorkbookToRoundToStudentWorkbookResponse['data'],
): Slice<GroupStudentWorkbook> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_GroupStudentWorkbookWireSchema), asWire(raw), ENTITY),
    toGroupStudentWorkbook,
  )

export const adaptGroupWorkbookPageProgresses = (
  raw: CommonResponseSliceGroupWorkbookPageToStudentWorkbookProgressResponse['data'],
): Slice<GroupWorkbookPageProgress> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(GroupWorkbookPageProgressSchema), asWire(raw), ENTITY),
    (progress) => progress,
  )

export const adaptGroupWorkbookScoredProblems = (
  raw: CommonResponseSliceGroupWorkbookProblemToStudentWorkbookScoringAndStudentResponse['data'],
): Slice<GroupWorkbookScoredProblem> =>
  mapSliceContent(
    parseApiResponse(sliceSchema(_GroupWorkbookScoredProblemWireSchema), asWire(raw), ENTITY),
    toWorkbookProblemDetail,
  )

export const adaptStudentWorkbookProgressStatus = (
  raw: SetProgressOfPageData['data'],
): GroupWorkbookPageProgress['status'] =>
  parseApiResponse(GroupWorkbookPageProgressSchema.shape.status, raw, ENTITY)
